import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Alert, Form, Table } from 'react-bootstrap';
import {
    DialogContent,
    makeStyles,
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import { NO_DATA } from 'components/modules/customer/merge/mergeFieldConfig';

const CONFIRM_WORD = 'MERGE';

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(2.5, 3, 2),
        overflow: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    warning: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        marginBottom: 0,
    },
    warningIcon: {
        color: 'inherit',
        marginTop: 2,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1),
    },
    previewWrap: {
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        overflow: 'auto',
        maxHeight: 360,
    },
    table: {
        minWidth: 520,
        marginBottom: 0,
        '& th, & td': {
            verticalAlign: 'middle',
            padding: theme.spacing(0.75, 1.25),
        },
    },
    fieldLabel: {
        fontWeight: 700,
        whiteSpace: 'nowrap',
        width: 160,
    },
    value: {
        wordBreak: 'break-word',
    },
    empty: {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
    },
    source: {
        whiteSpace: 'nowrap',
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
    fromSecondary: {
        color: theme.palette.info.dark,
        fontWeight: 600,
    },
    confirmSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        maxWidth: 420,
    },
    confirmTextLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        marginBottom: 0,
    },
    confirmInput: {
        maxWidth: 320,
    },
}));

const DuplicateConfirmStep = ({
    active,
    masterCustomerCode,
    secondaryCount,
    previewRows,
    onCanConfirmChange,
}) => {
    const classes = useStyles();
    const [confirmText, setConfirmText] = useState('');
    const canConfirm = confirmText.trim().toUpperCase() === CONFIRM_WORD;

    useEffect(() => {
        if (active) setConfirmText('');
    }, [active]);

    useEffect(() => {
        if (onCanConfirmChange) onCanConfirmChange(canConfirm);
    }, [canConfirm, onCanConfirmChange]);

    return (
        <DialogContent className={classes.content}>
            <Alert variant="warning" className={classes.warning}>
                <WarningOutlinedIcon className={classes.warningIcon} fontSize="small" />
                <div>
                    {`You are about to permanently merge ${secondaryCount} secondary record${
                        secondaryCount === 1 ? '' : 's'
                    } into master #${masterCustomerCode}. This cannot be undone.`}
                </div>
            </Alert>

            <div className={classes.subtitle}>
                Preview of the values that will remain on the master customer:
            </div>

            <div className={classes.previewWrap}>
                <Table striped bordered hover size="sm" className={classes.table}>
                    <thead>
                        <tr>
                            <th className={classes.fieldLabel}>Field</th>
                            <th>Final Value</th>
                            <th>Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {previewRows.map((row) => {
                            const isEmpty = !row.value || row.value === NO_DATA;
                            return (
                                <tr key={row.key}>
                                    <td className={classes.fieldLabel}>{row.label}</td>
                                    <td className={isEmpty ? classes.empty : classes.value}>
                                        {isEmpty ? NO_DATA : row.value}
                                    </td>
                                    <td
                                        className={clsx(classes.source, !row.fromMaster && classes.fromSecondary)}
                                    >
                                        {row.fromMaster
                                            ? `Master #${row.sourceCustomerCode}`
                                            : `Secondary #${row.sourceCustomerCode}`}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <div className={classes.confirmSection}>
                <div className={classes.confirmTextLabel}>
                    {`Type ${CONFIRM_WORD} to confirm.`}
                </div>
                <Form.Control
                    type="text"
                    size="sm"
                    className={classes.confirmInput}
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    placeholder={CONFIRM_WORD}
                    autoFocus
                />
            </div>
        </DialogContent>
    );
};

DuplicateConfirmStep.propTypes = {
    active: PropTypes.bool.isRequired,
    masterCustomerCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    secondaryCount: PropTypes.number.isRequired,
    previewRows: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string,
        sourceCustomerCode: PropTypes.number,
        fromMaster: PropTypes.bool,
    })).isRequired,
    onCanConfirmChange: PropTypes.func,
};

DuplicateConfirmStep.defaultProps = {
    onCanConfirmChange: null,
};

export default DuplicateConfirmStep;
